// Asset Library — reusable tenant-shared media library.
// Two modes:
//   • Full-page (embedded in Settings) — CRUD grid + usage.
//   • Picker modal — { onPick(url), accept } for use in Proposal Builder.
//
// Backing endpoints: /api/assets (GET), /api/assets/usage (GET),
// /api/assets/{id} (PATCH, DELETE), /api/proposals/upload (POST).

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Upload, Trash2, Pencil, Image as ImageIcon, FileText, Search, Loader2,
  X, Check, AlertTriangle, HardDrive,
} from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { useConfirm } from "./ConfirmModal";

// ---------- helpers ---------------------------------------------------------
export function formatBytes(n) {
  if (!n || n < 1024) return `${n || 0} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function absoluteUrl(u) {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  return `${window.location.origin}${u}`;
}

// ---------- Usage bar --------------------------------------------------------
export function AssetUsageBar({ usage, compact = false }) {
  if (!usage) return null;
  const pct = Math.min(100, usage.percent_used || 0);
  const nearFull = pct >= 80;
  const full = pct >= 100;
  const color = full ? "#dc2626" : nearFull ? "#f59e0b" : "var(--pg-accent)";
  return (
    <div data-testid="asset-usage-bar" style={{ minWidth: compact ? 180 : 260 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 500, color: "var(--pg-text)" }}>
          <HardDrive size={12} /> Storage
        </div>
        <div className="mono" style={{ fontSize: 11, color: "var(--pg-text-mute)" }}>
          {formatBytes(usage.used_bytes)} / {formatBytes(usage.limit_bytes)}
        </div>
      </div>
      <div style={{ height: 6, background: "var(--pg-surface-alt, rgba(0,0,0,0.06))", borderRadius: 3, overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: color, transition: "width 240ms" }} />
      </div>
      {nearFull && !compact && (
        <div style={{ marginTop: 8, fontSize: 11.5, color, display: "flex", gap: 6, alignItems: "flex-start" }}>
          <AlertTriangle size={12} style={{ marginTop: 1, flexShrink: 0 }} />
          <span>
            {full
              ? "Storage full — delete unused assets or contact support to upgrade."
              : `Storage ${pct.toFixed(0)}% used. Consider tidying up soon.`}
          </span>
        </div>
      )}
    </div>
  );
}

// ---------- Grid item -------------------------------------------------------
function AssetTile({ asset, selected, pickable, onPick, onRename, onDelete }) {
  const isImage = (asset.content_type || "").startsWith("image/");
  const url = absoluteUrl(asset.url);
  return (
    <div
      data-testid={`asset-tile-${asset.id}`}
      onClick={() => pickable && onPick?.(asset)}
      style={{
        position: "relative", border: `1px solid ${selected ? "var(--pg-accent)" : "var(--pg-border)"}`,
        borderRadius: 10, overflow: "hidden", background: "var(--pg-surface)",
        cursor: pickable ? "pointer" : "default",
        boxShadow: selected ? "0 0 0 2px var(--pg-accent-soft)" : "none",
        transition: "border-color 140ms, box-shadow 140ms",
      }}
    >
      <div style={{ aspectRatio: "4 / 3", background: "var(--pg-surface-alt, rgba(0,0,0,0.03))", display: "flex", alignItems: "center", justifyContent: "center" }}>
        {isImage ? (
          <img src={url} alt={asset.original_filename}
               style={{ width: "100%", height: "100%", objectFit: "cover" }} loading="lazy" />
        ) : (
          <div style={{ textAlign: "center", color: "var(--pg-text-mute)" }}>
            <FileText size={30} style={{ margin: "0 auto 6px" }} />
            <div className="mono text-[10px] uppercase tracking-wider">PDF</div>
          </div>
        )}
      </div>
      <div style={{ padding: "8px 10px" }}>
        <div style={{ fontSize: 12, fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: "var(--pg-text)" }}
             title={asset.original_filename}>
          {asset.original_filename}
        </div>
        <div className="mono" style={{ fontSize: 10, color: "var(--pg-text-mute)", marginTop: 2 }}>
          {formatBytes(asset.size)}
        </div>
      </div>
      {selected && (
        <div style={{
          position: "absolute", top: 6, left: 6, background: "var(--pg-accent)", color: "#fff",
          borderRadius: "50%", width: 22, height: 22, display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <Check size={13} />
        </div>
      )}
      {(onRename || onDelete) && (
        <div style={{ position: "absolute", top: 6, right: 6, display: "flex", gap: 4 }}
             onClick={(e) => e.stopPropagation()}>
          {onRename && (
            <button data-testid={`asset-rename-${asset.id}`} onClick={() => onRename(asset)}
                    title="Rename"
                    style={tileBtn}>
              <Pencil size={12} />
            </button>
          )}
          {onDelete && (
            <button data-testid={`asset-delete-${asset.id}`} onClick={() => onDelete(asset)}
                    title="Delete"
                    style={{ ...tileBtn, color: "#fff", background: "rgba(220,38,38,0.85)" }}>
              <Trash2 size={12} />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

const tileBtn = {
  padding: 5, borderRadius: 6,
  background: "rgba(15,23,42,0.7)", color: "#fff",
  border: "none", cursor: "pointer",
  display: "flex", alignItems: "center", justifyContent: "center",
  backdropFilter: "blur(4px)",
};

// ---------- Main library component (page or picker) -------------------------
export default function AssetLibrary({
  mode = "manage",       // "manage" | "picker"
  accept = "image",      // "image" | "pdf" | "any"
  onPick,                // (asset) => void
  onClose,               // for picker mode
}) {
  const [assets, setAssets] = useState([]);
  const [usage, setUsage] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState(accept === "pdf" ? "pdf" : accept === "image" && mode === "picker" ? "image" : "");
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);
  const confirm = useConfirm();

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (filter) params.set("kind", filter);
      params.set("limit", "60");
      const [list, use] = await Promise.all([
        api.get(`/assets?${params.toString()}`),
        api.get("/assets/usage"),
      ]);
      setAssets(list.data.items || []);
      setUsage(use.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load asset library");
    } finally { setLoading(false); }
  }, [q, filter]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const acceptString = useMemo(() => {
    if (accept === "pdf") return "application/pdf";
    if (accept === "image") return "image/png,image/jpeg,image/webp,image/gif,image/svg+xml";
    return "image/*,application/pdf";
  }, [accept]);

  const upload = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    let lastAsset = null;
    for (const file of files) {
      // Validate against acceptString
      if (accept === "image" && !file.type.startsWith("image/")) {
        toast.error(`${file.name} skipped — images only`);
        continue;
      }
      if (accept === "pdf" && file.type !== "application/pdf") {
        toast.error(`${file.name} skipped — PDFs only`);
        continue;
      }
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/proposals/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        lastAsset = {
          id: data.file_id,
          url: data.url,
          original_filename: data.filename,
          size: data.size,
          content_type: data.content_type,
        };
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || `Upload failed: ${file.name}`);
      }
    }
    setUploading(false);
    await loadAll();
    // In picker mode auto-select the last uploaded asset
    if (mode === "picker" && lastAsset) setSelectedId(lastAsset.id);
    if (mode === "manage" && lastAsset) toast.success("Uploaded to asset library");
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    upload(Array.from(e.dataTransfer.files || []));
  };

  const handleRename = async (asset) => {
    const val = await confirm({
      title: "Rename asset",
      confirmLabel: "Save",
      input: { label: "Filename", placeholder: asset.original_filename, required: true },
    });
    if (!val || val === asset.original_filename) return;
    try {
      await api.patch(`/assets/${asset.id}`, { original_filename: val });
      toast.success("Renamed");
      loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Rename failed");
    }
  };

  const handleDelete = async (asset) => {
    const ok = await confirm({
      title: "Delete this asset?",
      message:
        `"${asset.original_filename}" will be removed from the library.\n\n` +
        "Any existing proposals that reference this file will show a broken link — this cannot be undone.",
      destructive: true,
      confirmLabel: "Delete",
    });
    if (!ok) return;
    try {
      await api.delete(`/assets/${asset.id}`);
      toast.success("Asset deleted");
      loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  const isPicker = mode === "picker";
  const pickable = isPicker;

  return (
    <div data-testid="asset-library">
      {/* Toolbar */}
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 14 }}>
        <div style={{ position: "relative", flex: "1 1 240px", maxWidth: 320 }}>
          <Search size={13} style={{ position: "absolute", left: 10, top: 10, color: "var(--pg-text-mute)" }} />
          <input
            className="pg-input"
            style={{ paddingLeft: 30, width: "100%" }}
            placeholder="Search filename…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="asset-search"
          />
        </div>
        {accept === "any" && (
          <div style={{ display: "flex", gap: 4 }}>
            {[
              { k: "", label: "All" },
              { k: "image", label: "Images" },
              { k: "pdf", label: "PDFs" },
            ].map(f => (
              <button key={f.k}
                      onClick={() => setFilter(f.k)}
                      className={`pg-btn ${filter === f.k ? "pg-btn-active" : ""}`}
                      data-testid={`asset-filter-${f.k || "all"}`}
                      style={{ fontSize: 12, padding: "6px 12px" }}>
                {f.label}
              </button>
            ))}
          </div>
        )}
        <div style={{ flex: 1 }} />
        <button
          onClick={() => fileRef.current?.click()}
          disabled={uploading || (usage && usage.percent_used >= 100)}
          className="pg-btn pg-btn-primary"
          data-testid="asset-upload-btn"
          style={{ fontSize: 12 }}
        >
          {uploading ? <><Loader2 size={12} className="animate-spin" /> Uploading…</> : <><Upload size={12} /> Upload</>}
        </button>
        <input
          ref={fileRef}
          type="file"
          multiple
          accept={acceptString}
          style={{ display: "none" }}
          onChange={(e) => upload(Array.from(e.target.files || []))}
        />
      </div>

      {/* Usage */}
      {usage && (
        <div style={{ marginBottom: 16 }}>
          <AssetUsageBar usage={usage} />
        </div>
      )}

      {/* Grid area (drop zone) */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        style={{
          border: `2px dashed ${dragOver ? "var(--pg-accent)" : "transparent"}`,
          borderRadius: 12, padding: dragOver ? 8 : 0,
          transition: "border-color 140ms, padding 140ms",
          minHeight: 200,
        }}
      >
        {loading ? (
          <div style={{ padding: 60, textAlign: "center", color: "var(--pg-text-mute)" }}>
            <Loader2 size={22} className="animate-spin" style={{ margin: "0 auto 8px" }} />
            Loading library…
          </div>
        ) : assets.length === 0 ? (
          <div style={{ padding: "50px 20px", textAlign: "center" }} data-testid="asset-empty-state">
            <ImageIcon size={30} style={{ color: "var(--pg-text-mute)", margin: "0 auto 10px" }} />
            <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>
              {q || filter ? "No assets match your filter" : "Your asset library is empty"}
            </div>
            <div style={{ fontSize: 12.5, color: "var(--pg-text-mute)", maxWidth: 340, margin: "0 auto" }}>
              Drop files here, or click <b>Upload</b>. Everything you upload from proposals lands here automatically for reuse.
            </div>
          </div>
        ) : (
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
            gap: 12,
          }}>
            {assets.map(a => (
              <AssetTile
                key={a.id}
                asset={a}
                selected={selectedId === a.id}
                pickable={pickable}
                onPick={(asset) => setSelectedId(asset.id)}
                onRename={!isPicker ? handleRename : undefined}
                onDelete={!isPicker ? handleDelete : undefined}
              />
            ))}
          </div>
        )}
      </div>

      {/* Picker footer */}
      {isPicker && (
        <div style={{
          display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16,
          paddingTop: 16, borderTop: "1px solid var(--pg-border)",
        }}>
          <button className="pg-btn" onClick={onClose} data-testid="asset-picker-cancel">Cancel</button>
          <button
            className="pg-btn pg-btn-primary"
            disabled={!selectedId}
            data-testid="asset-picker-select"
            onClick={() => {
              const a = assets.find(x => x.id === selectedId);
              if (a) { onPick?.(a); onClose?.(); }
            }}
          >
            Use this asset
          </button>
        </div>
      )}
    </div>
  );
}

// ---------- Modal wrapper for picker ---------------------------------------
export function AssetPickerModal({ open, onClose, accept = "image", onPick, title = "Asset library" }) {
  if (!open) return null;
  return (
    <>
      <div className="side-panel-backdrop" onClick={onClose} data-testid="asset-picker-backdrop" />
      <div
        role="dialog" aria-modal="true"
        className="pg-card"
        data-testid="asset-picker-modal"
        style={{
          position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
          width: "min(900px, 94vw)", maxHeight: "88vh", overflowY: "auto",
          padding: 0, zIndex: 210, borderRadius: 12,
          boxShadow: "0 20px 40px rgba(0,0,0,0.25)",
        }}
      >
        <div style={{ padding: "18px 22px", borderBottom: "1px solid var(--pg-border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>{title}</h3>
          <button onClick={onClose} className="pg-btn pg-btn-ghost" style={{ padding: 6 }} data-testid="asset-picker-close">
            <X size={16} />
          </button>
        </div>
        <div style={{ padding: 22 }}>
          <AssetLibrary mode="picker" accept={accept} onPick={onPick} onClose={onClose} />
        </div>
      </div>
    </>
  );
}
